import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { UserAuthService } from './user-auth/user-auth.service';
import { ToastService } from './toast.service';
import { AppConstants, ObservationEvents } from '../utilities/constant';
import { EventProvider } from './event';
import { BackendCallService } from './backend-call/backend-call.service';
import { LoggingService } from './logging.service';
import { LoaderService } from './loader/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiUrls } from '../utilities/api-urls';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private noLoaderApis = [
    'https://graph.microsoft.com/v1.0/groups',
    'https://graph.microsoft.com/v1.0/sites',
    'https://graph.microsoft.com/v1.0/teams',
    'https://graph.microsoft.com/v1.0/users',
    'https://graph.microsoft.com/v1.0/me',
    'https://dna-westeur',
    'https://graph.microsoft.com/v1.0/teamsTemplates',
    'https://login.microsoftonline.com/organizations',
    'https://login.microsoftonline.com/common'
  ];
  private ongoingRequests = 0;
  private errorLoggingInProgress: boolean;

  constructor(
    private loader: LoaderService,
    private userAuthService: UserAuthService,
    private toastService: ToastService,
    private eventProvider: EventProvider,
    private backendService: BackendCallService,
    private loggingService: LoggingService,
    private translateService: TranslateService
  ) {}

  private getBadRequestErrorMessage(error: HttpErrorResponse): string {
    const errorBody = error?.error || {};
    const errors = this.normalizeErrorMessages(errorBody.errors);

    if (errors.length) {
      return errorBody?.message ? `${errorBody.message}: ${errors.join(' ')}` : errors.join(' ');
    }

    return errorBody?.message || this.translateService.instant('something_went_wrong');
  }

  private normalizeErrorMessages(errors: any): string[] {
    if (Array.isArray(errors)) {
      return errors.map((errorMessage) => `${errorMessage || ''}`.trim()).filter(Boolean);
    }

    if (typeof errors === 'string') {
      return errors.trim() ? [errors.trim()] : [];
    }

    return [];
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.userAuthService.getAccessToken();
    const excludedLoaderApis = [
      ...this.noLoaderApis, // Existing no loader APIs
      'offersvc/mastermachinesvc/filteredmachines',
      '/api/user/users',
      '/api/role/roles',
      '/api/group/groupwithusercount',
      '/api/customersvc/GetAllSalesOrganization',
      '/api/offersvc/service/getallservicematerial',
      '/api/customersvc/GetAllCustomer',
      '/api/customersvc/GetAllContact',
      '/projectsvc/projectsv2/',
      '/ordersvc/ordersv2/',
      'api/projectsvc/projectsv2/',
      '/api/offersvc/v2/',
      '/notificationhub/negotiate?negotiateVersion=1',
      '/finalquoteversions',
      '/commercialquoteversions',
      '/api/customersvc/menuItems',
      '/day/dailymetrics',
      '/api/ordersvc/effortsmetrics',
      '/api/offersvc/effortmetrics',
      '/api/projectsvc/projectmetrics',
      '/api/projectsvc/companydetails',
      '/api/riskassessmentsvc/SaveStaticTemplateV1',
      'api/riskassessmentsvc/extendroadmapweb/v2',
      'api/riskassessmentsvc/GetRoadmapExecutionSections/',
      'api/librarysvc/roadmapsvc/',
      'api/offersvc/librarysvc/',
      'api/riskassessmentsvc/GetStaticTemplateDetails/',
      'api/riskassessmentsvc/GetConfigureRoadmapFilterDropdown',
      'api/riskassessmentsvc/getfilteredroadmaps',
      'offersvc/mastermachinesvc/GetModes/',
       '/ProcessHazardModeMappings',
      '/configuredroadmaps',
      '/sistemareport',
      '/api/projectsvc/auditlogs',
      '/api/riskassessmentsvc/GetRAReports',
      '/api/group/group/filter',
      '/api/userpermissionassociation',
      // AI writing-assist calls (proofread/rewrite/generate) have their own
      // inline button/bubble loading states — a full-page overlay on top of
      // those is redundant and, since these calls run an LLM, noticeably
      // blocks the page longer than the app's usual fast calls.
      '/api/ai-writing/',
      '/api/soplibrarysvc/'
    ];

    const shouldShowLoader = !excludedLoaderApis.some(api => req.url.includes(api));
    const isSopLibraryRequest = req.url.includes('/api/soplibrarysvc/');
    const isRaReportDownloadRequest = req.url.includes('/api/riskassessmentsvc/riskassessmentreport/')
      && req.url.endsWith('/download');
    const isHazardMeasureSaveRequest = req.method === 'POST'
      && (req.url === ApiUrls.counterMeasure || req.url === ApiUrls.IntialHazard);

    // Show loader only if the request requires it
    if (shouldShowLoader) {
      this.ongoingRequests++;
      if (this.ongoingRequests === 1) {
        this.loader.show();
      }
    }

    // Add token to all outgoing requests except for specific endpoints
    if (
      !req.url.startsWith(environment.tokenEndpoint + '/Token') &&
      !req.url.startsWith(environment.tokenEndpoint + '/refresh-token') &&
      !req.url.startsWith('https://graph.microsoft.com/v1.0/')
    ) {
      if (token) {
        req = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isRaReportDownloadRequest) {
          return throwError(error);
        }
        if (this.errorLoggingInProgress && !isHazardMeasureSaveRequest) {
          return throwError(error);
        }
        if (!this.errorLoggingInProgress) {
          this.errorLoggingInProgress = true;
          const payload = {
            errorMessage: error.message,
            stackTrace: '',
            controllerName: '',
            actionName: '',
            url: req.url,
            request: null,
            response: '',
            userId: this.userAuthService.getUserList().userId
          };
          this.loggingService.logError(payload).subscribe(() => {
            this.errorLoggingInProgress = false;
          }, () => {
            this.errorLoggingInProgress = false;
          });
        }
        // Upload/Add handlers own these error toasts; authentication remains global.
        if (isHazardMeasureSaveRequest && error.status !== 401) {
          return throwError(error);
        }
        this.eventProvider.BroadcastEvent(
          ObservationEvents.ERROR_OCCURRED,
          isHazardMeasureSaveRequest ? { toastShown: true } : error
        );
        if (error.status === 500) {
          if (!isSopLibraryRequest) {
            this.toastService.presentToast(error.error?.message, 4000, AppConstants.ERROR_SNACKBAR, 'top', 'error_occurred');
          }
        } else if (error.status === 504) {
          const errorMessage = this.translateService.instant('gateway_timeout_msg');
          if (!isSopLibraryRequest) {
            this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, 'top');
          }
          this.eventProvider.BroadcastEvent(ObservationEvents.GATEWAY_TIMEOUT, true);
        } else if (error.status === 409) {
           // validatemachinename 409s are shown as a localized toast by the caller.
          if (!isSopLibraryRequest && !req.url.includes('/validatemachinename')) {
           this.toastService.presentToast(error.error?.message, 4000, AppConstants.ERROR_SNACKBAR, 'top', 'error_occurred');
          }
        }
        else if (error.status === 400 && !error.url.includes('graph.microsoft.com') && !isSopLibraryRequest) {
          this.toastService.presentToast(this.getBadRequestErrorMessage(error), 4000, AppConstants.ERROR_SNACKBAR, 'top', 'error_occurred');
        } else if (error.status === 401) {
          const errorMessage = this.translateService.instant('session_expired_msg');
          this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
          this.userAuthService.logOut();
        } else if (error.status === 0) {
          if (!isSopLibraryRequest) {
            const errorMessage = this.translateService.instant('network_error_msg');
            this.toastService.presentToast(errorMessage, 4000, AppConstants.ERROR_SNACKBAR, AppConstants.TOP);
          }
        }
        return throwError(error);
      }),
      finalize(() => {
        if (shouldShowLoader) {
          this.ongoingRequests--;
          if (this.ongoingRequests === 0) {
            this.loader.hide();
          }
        }
      })
    );
  }
}
